/**
 * usePreviousPeriodMetrics
 *
 * Fetches KPI totals for the window immediately before the selected period, so
 * surfaces can show a true previous-period delta ("up 15% on the previous 7
 * days") instead of a within-range trend.
 *
 * - Two requests in parallel, both with explicit `start`/`end`:
 *   `POST properties/aggregate/traffic/summary` (totals → deltas) and
 *   `POST properties/aggregate/traffic/trends` (buckets → the dashed
 *   previous-period line on the Overview chart). Same filters and property set
 *   as the page's own fetch, so workspace mode and URL filters are respected.
 *   The trends call runs only with `withSeries`; if it fails, only the line
 *   drops and the deltas still render.
 * - Window: see `getPreviousPeriodWindow` (7d → the 7 days before; mtd → same
 *   days last month; custom → equal length before start; 1d → previous 24h).
 * - Never blocks the page: callers render without deltas while this loads, and
 *   hide deltas on error. `data` is null in both cases, never a fake zero.
 * - Revenue is not covered: the revenue endpoint only takes preset periods.
 *
 * ```ts
 * const prev = usePreviousPeriodMetrics({ propertyIds, period, filters, token })
 * computePeriodDelta(currentVisitors, prev.data?.visitors) // signed % | null
 * ```
 */
import { useMemo } from 'react'
import { useQuery } from '@tanstack/react-query'
import {
  getAggregateTrafficSummaryForRange,
  getAggregateTrafficTrendsForRange,
  parseGranularityPeriod,
} from '@/lib/api/external-api'
import { transformToLegacy } from '@/lib/api/bridge/transformers'
import { filterClausesToParams } from '@/lib/api/bridge'
import type { FilterClause } from '@/lib/routing/types'
import {
  getPreviousPeriodWindow,
  previousPeriodPhrase,
  summaryToPeriodTotals,
  type PeriodTotals,
  type PeriodWindow,
} from '@/lib/utils/previousPeriod'

/** The demo property renders generated data; there is no real prior window. */
const DEMO_PROPERTY_ID = '00000'

export interface UsePreviousPeriodMetricsInput {
  /** One id for a site, several for workspace aggregate mode */
  propertyIds: string[]
  /** Frontend period: '1d' | '7d' | '30d' | '90d' | '12m' | 'mtd' | 'ytd' | 'YYYY-MM-DD:YYYY-MM-DD' */
  period: string
  /** URL filter clauses, same as the page's own fetch */
  filters?: FilterClause[]
  token?: string | null
  enabled?: boolean
  /** Also fetch the previous window's buckets (Overview chart only) */
  withSeries?: boolean
}

/** Previous-window buckets, oldest first, aligned by index with the current series */
export interface PeriodSeries {
  visitors: number[]
  views: number[]
}

export interface UsePreviousPeriodMetricsResult {
  /** Totals for the previous window; null while loading, on error, or when disabled */
  data: PeriodTotals | null
  /** Per-bucket values for the previous window; null when unavailable */
  series: PeriodSeries | null
  /** The window that was (or will be) queried */
  window: PeriodWindow | null
  /** "the previous 7 days", "the same days last month", ... */
  phrase: string
  isLoading: boolean
  isError: boolean
}

/** Current window exactly as the page's own fetch resolves it. */
function resolveCurrentWindow(period: string): PeriodWindow | null {
  try {
    const { start, end } = parseGranularityPeriod(transformToLegacy.period(period))
    return { start, end }
  } catch {
    return null
  }
}

/** Bucket size the page's own trends fetch uses for this period. */
function resolveGranularity(period: string): string {
  try {
    return parseGranularityPeriod(transformToLegacy.period(period)).granularity
  } catch {
    return 'day'
  }
}

export function usePreviousPeriodMetrics({
  propertyIds,
  period,
  filters = [],
  token,
  enabled = true,
  withSeries = false,
}: UsePreviousPeriodMetricsInput): UsePreviousPeriodMetricsResult {
  const idsKey = useMemo(() => [...propertyIds].filter(Boolean).sort().join(','), [propertyIds])
  const filtersKey = JSON.stringify(filters)

  const canFetch =
    enabled &&
    !!token &&
    idsKey.length > 0 &&
    !idsKey.split(',').includes(DEMO_PROPERTY_ID)

  const query = useQuery({
    // Key on the period, not on timestamps: rolling windows move with `now`
    // and must not spin up a new query on every render.
    queryKey: ['previous-period-metrics', idsKey, period, filtersKey, withSeries],
    queryFn: async () => {
      const current = resolveCurrentWindow(period)
      const previous = current ? getPreviousPeriodWindow(period, current) : null
      if (!current || !previous) throw new Error('Unresolvable comparison window')
      const ids = idsKey.split(',')
      const filterParams = filterClausesToParams(JSON.parse(filtersKey) as FilterClause[])
      const [summary, trends] = await Promise.all([
        getAggregateTrafficSummaryForRange(ids, previous, token as string, undefined, filterParams),
        withSeries
          ? getAggregateTrafficTrendsForRange(
              ids,
              { ...previous, granularity: resolveGranularity(period) },
              token as string,
              undefined,
              filterParams,
            ).catch(() => null)
          : Promise.resolve(null),
      ])
      const buckets = [...(trends?.data ?? [])].sort((a, b) => a.bucket_start.localeCompare(b.bucket_start))
      const series: PeriodSeries | null = buckets.length
        ? {
            visitors: buckets.map((b) => Number(b.unique_visitors) || 0),
            views: buckets.map((b) => Number(b.page_views) || 0),
          }
        : null
      return { totals: summaryToPeriodTotals(summary?.data), series, current, previous }
    },
    enabled: canFetch,
    staleTime: 5 * 60 * 1000,
    gcTime: 15 * 60 * 1000,
    retry: 1,
    refetchOnWindowFocus: false,
  })

  const currentWindow = query.data?.current ?? resolveCurrentWindow(period)

  return {
    data: query.data?.totals ?? null,
    series: query.data?.series ?? null,
    window: query.data?.previous ?? null,
    phrase: previousPeriodPhrase(period, currentWindow),
    isLoading: canFetch && query.isLoading,
    isError: query.isError,
  }
}

export { computePeriodDelta, computePointDelta } from '@/lib/utils/previousPeriod'
export type { PeriodTotals, PeriodWindow } from '@/lib/utils/previousPeriod'
